export const GIST_REPORT_EVENT = "geniusintelligencescaletest";

export const normalizeDetailed = (detailed) =>
  (detailed || "").toLowerCase().trim().replace(/\s+/g, " ");

export const formatDetailedLabel = (detailed) =>
  normalizeDetailed(detailed)
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .toUpperCase();

export const getStanineColor = (stanine) => {
  const score = Number(stanine);
  if (score >= 1 && score <= 3) return "#B7295A";
  if (score > 3 && score <= 6) return "#C4A200";
  if (score > 6 && score <= 8) return "#00966D";
  if (score === 9) return "#23628D";
  return "#4E4B66";
};

/** Header bar + badge colors for domain detail blocks (by `detailed` from API). */
const DETAILED_HEADER_STYLES = {
  "extremely low": { background: "#FCE8EF", color: "#B7295A" },
  "very low": { background: "#FCE8EF", color: "#B7295A" },
  low: { background: "#FCE8EF", color: "#B7295A" },
  "low average": { background: "#FFF7D4", color: "#C4A200" },
  average: { background: "#FFED9D", color: "#9A7B00" },
  "high average": { background: "#E3F6EF", color: "#00966D" },
  high: { background: "#E3F6EF", color: "#00966D" },
  "very high": { background: "#E1F2FF", color: "#23628D" },
  "extremely high": { background: "#E1F2FF", color: "#23628D" },
};

export const getDetailedHeaderStyle = (detailed) => {
  const key = normalizeDetailed(detailed);
  return (
    DETAILED_HEADER_STYLES[key] || {
      background: "#FFF7D4",
      color: "#14142B",
    }
  );
};

export const formatPercentile = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toFixed(2)}%`;
};

export const formatIqRange = (range) => {
  if (!range) return "-";
  const min = Math.round(range.min);
  const max = Math.round(range.max);
  return `${min}-${max}`;
};

export const getWitAdvancedReport = (reportData) => {
  const advanced = reportData?.advanced_report;
  if (Array.isArray(advanced) && advanced.length > 0) {
    return advanced[0];
  }
  return null;
};

export const isWitAdvancedReportReady = (data) => {
  const advanced = getWitAdvancedReport(data);
  return (
    Array.isArray(advanced?.composite_score_summary) &&
    advanced.composite_score_summary.length > 0
  );
};

export const hasBasicReport = (data) => Boolean(data?.basic_report);

// --- Static copy & domain metadata (UI text + "what your report says" per level) ---

export const WIT_INTRO_TITLE = "WIZKLUB INTELLIGENCE TEST (WIT)";

export const WIT_INTRO_DESCRIPTION =
  "The WIT is a comprehensive test comprising 45 questions, divided into 9 sections, that evaluates a child's intellectual ability on the five discrete cognitive domains of Fluid Reasoning, Pattern Recognition, Visual Spatial Processing, Data Analysis and Quantitative Reasoning. It generates a Full Scale IQ (formerly known as an intelligence quotient or IQ score) that represents a child's general intellectual ability. It also provides five primary index scores in the aforementioned five cognitive domains.";

export const WIT_NEED_FOR_ASSESSMENT =
  "IQ is important in understanding abilities, potential and overall achievement. Understanding an individual's overall ability level is important in understanding how they can and will function in an academic setting and helps parents and teachers to understand their capabilities with regard to academics and life in general. It can also be used to select career paths that are co-related to the areas of strength.";

export const WIT_CLOSING_NOTE =
  "The scores in the five discrete domains represent the test-taker's current ability. The score is only indicative of the current proficiency levels. The skills of fluid reasoning, pattern recognition, visual-spatial processing, quantitative reasoning and data analysis can all be improved upon with timely interventions and learning programs.";

export const STANINE_NOTE =
  'Note: A stanine ("Standard Nine") score is a way to scale scores on a nine-point scale.';

export const DOMAIN_CARD_THEMES = {
  FLR: { accent: "#B7295A", light: "rgba(183, 41, 90, 0.35)" },
  PRN: { accent: "#3C8DCB", light: "rgba(60, 141, 203, 0.35)" },
  VSP: { accent: "#7B61FF", light: "rgba(123, 97, 255, 0.35)" },
  QTR: { accent: "#00966D", light: "rgba(0, 150, 109, 0.35)" },
  DAC: { accent: "#23628D", light: "rgba(35, 98, 141, 0.35)" },
};

const DOMAIN_ORDER = ["FLR", "PRN", "VSP", "QTR", "DAC"];

const WIT_DOMAIN_META = {
  FLR: {
    abbreviation: "FLR",
    name: "Fluid Reasoning",
    title: "FLUID REASONING",
    whatIs:
      "Fluid reasoning (FLR) measures the capacity to think logically and solve problems in language and context. A high score in fluid reasoning is an indication of enhanced abilities in connecting visuals to words, finding the relationship between words and terms and using that to find similar relationship patterns. Fluid reasoning also measures the ability to understand contextual clues in language and thereby spot gaps in logical progression of ideas. An individual adept at fluid reasoning will be able to critically examine statements and propositions.",
    reportSays: {
      "extremely low":
        "Your score indicates a well below average ability to understand contextual clues in language. You show significant difficulty in applying relationships between words or terms and in reasoning critically.",
      "very low":
        "Your score indicates a very low ability to understand contextual clues in language. You struggle to apply relationships between words or terms and show limited critical reasoning.",
      low: "Your score indicates a below average ability to understand contextual clues in language. You are not able to adequately understand and apply relationships between words or terms and show less than average ability to reason critically.",
      "low average":
        "Your score indicates a low average ability to understand contextual clues in language. You can apply some relationships between words or terms but need support to reason critically.",
      average:
        "Your score indicates an average ability to understand contextual clues in language. You can generally apply relationships between words or terms and reason with moderate consistency.",
      "high average":
        "Your score indicates a high average ability to understand contextual clues in language. You apply relationships between words or terms well and reason critically with growing confidence.",
      high: "Your score indicates an above average ability to understand contextual clues in language. You apply relationships between words or terms effectively and demonstrate solid critical reasoning.",
      "very high":
        "Your score indicates a very high ability to understand contextual clues in language. You excel at applying relationships between words or terms and demonstrate strong critical reasoning.",
      "extremely high":
        "Your score indicates an extremely high ability to understand contextual clues in language. You demonstrate exceptional skill in applying relationships between words or terms and in critical reasoning.",
    },
  },
  PRN: {
    abbreviation: "PRN",
    name: "Pattern Recognition",
    title: "PATTERN RECOGNITION (PRN)",
    whatIs:
      "Pattern Recognition (PRN) is the ability to see patterns in numbers, visuals and symbols. A high score in pattern recognition indicates superior abilities in connecting underlying logic and rationale and using it in a predictive manner. High ability in connecting patterns is indicative of strong logical reasoning and critical thinking skills and is often associated with high ability to simplify complex situations and solve problems.",
    reportSays: {
      "extremely low":
        "Your score indicates a well below average ability to spot patterns. You have significant difficulty making connections between objects and understanding underlying logic or sequence.",
      "very low":
        "Your score indicates a very low ability to spot patterns. You rarely make connections between objects or understand underlying logic or sequence.",
      low: "Your score indicates a below average ability to spot patterns. You are not able to adequately make connections between objects and understand the underlying logic or sequence.",
      "low average":
        "Your score indicates a low average ability to spot patterns. You can identify some connections between objects but need support with underlying logic or sequence.",
      average:
        "Your score indicates an average ability to spot patterns. You can make connections between objects and understand logic or sequence with moderate consistency.",
      "high average":
        "Your score indicates a high average ability to spot patterns. You make useful connections between objects and understand underlying logic or sequence well.",
      high: "Your score indicates an above average ability to spot patterns. You connect objects effectively and understand underlying logic or sequence with confidence.",
      "very high":
        "Your score indicates a very high ability to spot patterns. You excel at connecting objects and understanding underlying logic or sequence.",
      "extremely high":
        "Your score indicates an extremely high ability to spot patterns. You demonstrate exceptional skill in connecting objects and understanding underlying logic or sequence.",
    },
  },
  VSP: {
    abbreviation: "VSP",
    name: "Visual Spatial Processing",
    title: "VISUAL SPATIAL PROCESSING (VSP)",
    whatIs:
      "Visual Spatial Processing (VSP) measures the ability to perceive the visual world accurately. A high VSP score indicates advanced ability to construct mental representations of visual information. An individual with a high VSP score is able to make mental images of complex systems and use that to understand patterns and systems. Such an individual is also able to easily perform transformations and modifications on visualizations and use those representations to better visualize problems or even simplify them.",
    reportSays: {
      "extremely low":
        "Your score indicates a well below average perception of visualization and spatial reasoning. You have significant difficulty creating mental images or performing spatial transformations.",
      "very low":
        "Your score indicates a very low perception of visualization and spatial reasoning. You struggle to create mental images and perform spatial transformations.",
      low: "Your score indicates a less than adequate perception of visualization and spatial reasoning. You are not easily able to create strong mental images of things or systems and have difficulty in spatial transformations.",
      "low average":
        "Your score indicates a low average perception of visualization and spatial reasoning. You can create basic mental images but need support with spatial transformations.",
      average:
        "Your score indicates an average perception of visualization and spatial reasoning. You can create mental images and perform spatial transformations with moderate consistency.",
      "high average":
        "Your score indicates a high average perception of visualization and spatial reasoning. You create mental images well and handle spatial transformations with growing skill.",
      high: "Your score indicates an above average perception of visualization and spatial reasoning. You create strong mental images and perform spatial transformations effectively.",
      "very high":
        "Your score indicates a very high perception of visualization and spatial reasoning. You excel at mental imagery and spatial transformations.",
      "extremely high":
        "Your score indicates an extremely high perception of visualization and spatial reasoning. You demonstrate exceptional mental imagery and spatial transformation skills.",
    },
  },
  QTR: {
    abbreviation: "QTR",
    name: "Quantitative Reasoning",
    title: "QUANTITATIVE REASONING (QTR)",
    whatIs:
      "Quantitative Reasoning (QTR) measures the ability to use math and facts to solve problems in the real world. A high QTR score indicates not only the ability to use math but to use mathematical constructs and processes in non-math scenarios. Such an individual possesses superior ability in applying known facts to problems as well as in turning problems in the real world into math problems.",
    reportSays: {
      "extremely low":
        "Your score indicates a well below average ability to use math and facts to solve problems. You have significant difficulty applying known facts and mathematical principles.",
      "very low":
        "Your score indicates a very low ability to use math and facts to solve problems. You struggle to apply known facts and mathematical principles.",
      low: "Your score indicates a below average ability to use math and facts to solve problems. You are not adequately able to apply known facts and mathematical principles to modify and solve problems.",
      "low average":
        "Your score indicates a low average ability to use math and facts to solve problems. You can apply some known facts and mathematical principles with support.",
      average:
        "Your score indicates an average ability to use math and facts to solve problems. You can apply known facts and mathematical principles with moderate consistency.",
      "high average":
        "Your score indicates a high average ability to use math and facts to solve problems. You apply known facts and mathematical principles well in many contexts.",
      high: "Your score indicates an above average ability to use math and facts to solve problems. You apply known facts and mathematical principles effectively.",
      "very high":
        "Your score indicates a very high ability to use math and facts to solve problems. You excel at applying known facts and mathematical principles.",
      "extremely high":
        "Your score indicates an extremely high ability to use math and facts to solve problems. You demonstrate exceptional application of math and facts in problem solving.",
    },
  },
  DAC: {
    abbreviation: "DAC",
    name: "Data Analysis",
    title: "DATA ANALYSIS (DAC)",
    whatIs:
      "Data analysis (DAC) measures the ability to read and understand data and use it to make observations and draw inferences. The section measures the ability to extract useful information from data and draw inferences or make decisions based on it. A high score on the DAC indicates excellent ability to clean, transform, and model data to discover useful information for measuring and analysis. Such individuals are also quick to spot anomalies and inadequacies in data and thus possess high critical thinking skills.",
    reportSays: {
      "extremely low":
        "Your score indicates a well below average aptitude for handling data in raw or visual form. You have significant difficulty transforming visual data to numbers and vice versa.",
      "very low":
        "Your score indicates a very low aptitude for handling data in raw or visual form. You struggle to transform visual data to numbers and vice versa.",
      low: "Your score indicates a below average aptitude for handling data in raw or visual form. You are not easily able to transform visual data to numbers and/or vice-versa.",
      "low average":
        "Your score indicates a low average aptitude for handling data in raw or visual form. You can work with simple data transformations with support.",
      average:
        "Your score indicates an average aptitude for handling data in raw or visual form. You can transform visual data to numbers and vice versa with moderate consistency.",
      "high average":
        "Your score indicates a high average aptitude for handling data in raw or visual form. You handle data transformations well in familiar contexts.",
      high: "Your score indicates an above average aptitude for handling data in raw or visual form. You transform visual data to numbers and vice versa effectively.",
      "very high":
        "Your score indicates a very high aptitude for handling data in raw or visual form. You excel at data interpretation and transformation.",
      "extremely high":
        "Your score indicates an extremely high aptitude for handling data in raw or visual form. You demonstrate exceptional data analysis and critical thinking skills.",
    },
  },
};

export const sortCompositeScores = (summary = []) => {
  const orderMap = Object.fromEntries(
    DOMAIN_ORDER.map((abbr, index) => [abbr, index]),
  );
  return [...summary].sort(
    (a, b) =>
      (orderMap[a.abbreviation] ?? 99) - (orderMap[b.abbreviation] ?? 99),
  );
};

export const getDomainMeta = (abbreviation) =>
  WIT_DOMAIN_META[abbreviation] || {
    abbreviation,
    name: abbreviation,
    title: abbreviation,
    whatIs: "",
    reportSays: {},
  };

export const getWhatYourReportSays = (abbreviation, detailed) => {
  const key = normalizeDetailed(detailed);
  const meta = getDomainMeta(abbreviation);
  return (
    meta.reportSays[key] ||
    "Your score has been recorded for this cognitive domain."
  );
};
